import { FocusMonitor } from '@angular/cdk/a11y'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, Self } from '@angular/core'
import { FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms'
import { MatFormFieldControl } from '@angular/material/form-field'
import { ClinicService, Facility, isTrulyEmpty } from '@mmx/shared'
import { filter, first, orderBy } from 'lodash'
import { ReplaySubject, Subject } from 'rxjs'
import { takeUntil, tap } from 'rxjs/operators'

import { CustomMaterialInputControlComponent } from '../custom-field'

type Value = string | string[] | Facility | Facility[] | any

@Component({
  selector: 'mmx-facility-selector-input',
  templateUrl: './facility-selector.component.html',
  styleUrls: ['./facility-selector.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: FacilitySelectorFieldComponent,
    },
  ],
  standalone: false,
})
export class FacilitySelectorFieldComponent
  extends CustomMaterialInputControlComponent<Value>
  implements OnInit, OnDestroy
{
  locationFormControl = new FormControl<Value>(null)
  locations: Facility[] = []
  facilityIds: string[] = []
  private initialized = false
  filteredLocations: ReplaySubject<Facility[]> = new ReplaySubject<Facility[]>(
    1,
  )
  locationFilterCtrl = new FormControl<string>(null)
  @Input() facilityId: string | string[]
  @Input() fullResponse = false
  @Input() multiple = false
  @Input() selectAll = true
  @Output() selectionChange = new EventEmitter<Value>()

  @Input()
  get value(): Value | null {
    return this._value
  }
  set value(locations: Value | null) {
    this._value = locations
    this.updateControlValue()
    this.stateChanges.next()
  }

  get isMultiple(): boolean {
    return coerceBooleanProperty(this.multiple)
  }

  get isFull(): boolean {
    return coerceBooleanProperty(this.fullResponse)
  }

  protected _placeholder = 'common.facility'
  protected _value: Value

  private onDestroy$ = new Subject<void>()

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected parentForm: NgForm,
    @Optional() protected parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    private clinicService: ClinicService,
  ) {
    super(ngControl, parentForm, parentFormGroup, fm, elRef)
  }

  ngOnInit() {
    super.ngOnInit()

    this.clinicService.getLocations().pipe(takeUntil(this.onDestroy$)).subscribe((locations) => {
      this.locations = filter(orderBy(locations, 'name'), 'enabled')
      this.facilityIds = this.locations.map((f) => f.id)

      // if the value was a locationId, transform it into a location now that we loaded the locations
      if (!isTrulyEmpty(this._value) || !isTrulyEmpty(this.facilityId)) {
        this.writeValue(!isTrulyEmpty(this._value) ? this._value : this.facilityId)
      }

      this.filteredLocations.next(this.locations.slice())

      this.locationFilterCtrl.valueChanges.pipe(
        takeUntil(this.onDestroy$),
        tap((search) => this.filterLocation(search)),
      ).subscribe()

      this.locationFormControl.valueChanges.subscribe((value) => {
        this._value = value
        this.stateChanges.next()
        this.onChange(this.value)
        if (this.initialized) {
          this.selectionChange.emit(value)
        }
      })

      if (this.locations.length === 1 && !this.isMultiple) {
        this.locationFormControl.setValue(first(this.locations)?.id)
        this.locationFormControl.updateValueAndValidity()
      } else if (this.value != null) {
        this.updateControlValue()
      }

      this.initialized = true
    })
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  // The inner mat-select is driven by `locationFormControl`, independent of the
  // outer NgControl, so it ignored the bound control's disabled state. Propagate
  // it so a disabled facility control actually locks the select.
  override setDisabledState(disabled: boolean): void {
    super.setDisabledState(disabled)
    if (disabled) {
      this.locationFormControl.disable({ emitEvent: false })
    } else {
      this.locationFormControl.enable({ emitEvent: false })
    }
  }

  writeValue(value: Value) {
    if (!value) {
      this.value = value
      return
    }

    if (typeof value === 'string') {
      this.value = this.isFull ? this.locations.filter(location => location.id === value) : [value]
    } else if (value instanceof Facility) {
      this.value = this.isFull ? [value] : [value.id]
    } else if (value.id) { // Single object that's not a facility instance
      this.value = this.isFull ? [this.locations.find(location => location.id === value.id)] : [value.id]
    } else if (Array.isArray(value)) {
      // If isFull is true, set this.value as list of Locations, otherwise, set this.value as a list of ids

      if (value[0] instanceof Facility) { // Array of facilities
        if (this.isFull) {
          this.value = value
        } else {
          this.value = value.map(facility => facility.id)
        }
      } else if (typeof value[0] === 'string') { // Array of facilityIds
        if (this.isFull) {
          this.value = value.map(id => this.locations.find(location => location.id === id)).filter(v => v)
        } else {
          this.value = value
        }
      } else if (value[0]?.id) { // Array of plain objects
        if (this.isFull) {
          this.value = value.map((obj) => this.locations.find(location => location.id === obj.id)).filter(v => v)
        } else {
          this.value = value.map((obj) => obj.id).filter(v => v)
        }
      }
    }
  }

  private updateControlValue() {
    if (this.isMultiple) {
      this.locationFormControl.setValue(this._value)
    } else if (this._value != null) {
      this.locationFormControl.setValue(this._value[0])
    } else {
      this.locationFormControl.setValue(null)
    }

    this.locationFormControl.updateValueAndValidity()
  }

  private filterLocation(search?: string) {
    if (!this.locations) {
      return
    } else if (!search) {
      this.filteredLocations.next(this.locations.slice())
      return
    }

    search = search.toLowerCase()

    // filter the facilities
    this.filteredLocations.next(
      this.locations.filter(
        (location) => location.fullName.toLowerCase().includes(search),
      ),
    )
  }
}
